import intersection from "lodash-es/intersection";

import { resolveDefaultPaymentPeriod } from "app/common/after-trial/resolveDefaultPaymentPeriod";
import { swSettings } from "app/common/services/swSettings";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";

import type { ICurrency, IPackagePrice, IPackagesPrice } from "./types";

const currenciesList = {
    USD: {
        sign: "$",
        code: "USD",
        Alpha2Code: "us",
        name: "U.S. Dollar",
    },
    RUB: {
        sign: "₽",
        code: "RUB",
        Alpha2Code: "ru",
        name: "Russian Ruble",
    },
    UAH: {
        sign: "₴",
        code: "UAH",
        Alpha2Code: "ua",
        name: "Ukrainian hryvnia",
    },
    MXN: {
        sign: "MX$",
        code: "MXN",
        Alpha2Code: "mx",
        name: "Mexican peso",
    },
    JPY: {
        sign: "¥",
        code: "JPY",
        Alpha2Code: "jp",
        name: "Japanese yen",
    },
    ILS: {
        sign: "₪",
        code: "ILS",
        Alpha2Code: "il",
        name: "Israeli shekel",
    },
    AUD: {
        sign: "AU$",
        code: "AUD",
        Alpha2Code: "au",
        name: "Australian dollar",
    },
    GBP: {
        sign: "£",
        code: "GBP",
        Alpha2Code: "gb",
        name: "Pound sterling",
    },
    EUR: {
        sign: "€",
        code: "EUR",
        Alpha2Code: "eu",
        name: "Euro",
    },
};

export const getPriceByCurrency = (packagePrice: IPackagePrice[], currency: string): IPackagePrice => {
    let tempPackagePriceData = packagePrice?.find((item) => {
        return item.Currency === currency;
    });

    if (!tempPackagePriceData) {
        tempPackagePriceData = packagePrice?.find((item) => {
            return item.Currency === "USD";
        });
    }

    if (!tempPackagePriceData) {
        const error = new Error(
            `Cannot evaluate the item's price for the given currency (${currency}). Object: ${JSON.stringify(packagePrice)}`,
        );
        throw error;
    }

    return { ...tempPackagePriceData };
};

export const numberWithCommas = (x: number | string): string => {
    if (!Number.isNaN(x)) {
        // 2000000 -> 2,000,000
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    return `${x}`;
};

export const getDefaultCurrency = (availableCurrencies: string[], defaultCurrency: string): string => {
    if (availableCurrencies.includes(defaultCurrency)) {
        return defaultCurrency;
    }

    return availableCurrencies[0];
};

const getCurrenciesFromPackages = (packagesPrices: IPackagesPrice): string[] => {
    const allCurrenciesArray: string[] = [];

    for (const packageName in packagesPrices) {
        for (const { Currency } of packagesPrices[packageName]) {
            allCurrenciesArray.push(Currency);
        }
    }

    return allCurrenciesArray;
};

export const getAvailableCurrencies = (packagesPrices: IPackagesPrice, availableCurrencies: string[]): string[] => {
    const currenciesFromPackages: string[] = getCurrenciesFromPackages(packagesPrices);

    return intersection(availableCurrencies, currenciesFromPackages);
};

export const getCurrency = (currency: string): ICurrency => currenciesList[currency] || currenciesList["USD"];

export const getRelevantPackages = (packages: IPackagesPrice) => {
    return {
        CompetitiveIntelligenceMonthly:
            packages.CompetitiveIntelligenceMonthly || packages.PremiumSalesExtensionMonthly,
        CompetitiveIntelligenceAnnually:
            packages.CompetitiveIntelligenceAnnually || packages.PremiumSalesExtensionAnnually,
    };
};

export const getDefaultPaymentPeriod = () => {
    const isPeriodTest = GrowthbookService.growthbookInstance.isOn(TestsNames.PAYMENT_PERIOD);

    return resolveDefaultPaymentPeriod(swSettings, isPeriodTest);
};
